import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CollapseHeader from "../../../core/common/collapse-header";
import { DealService, DealRequest, Deal } from "./deal.service";
import DealForm from "./dealForm";
import { DealFormDataProvider } from "./dealFormDataContext";
import { all_routes } from "../../router/all_routes";
import {
  showSuccessAlert,
  showErrorAlert,
  showConfirmationAlert,
} from "../../uiInterface/base-ui/alertservice";
import {
  getLifecycleTypePluralName,
  getLifecycleTypeName,
} from "../../../utils/lifecycleUtils";
import { AuthContext } from "../../../context/authcontext";
import { useTenantChange } from "../../../hooks/useTenantChange";
import {
  DealKanbanService,
  KanbanDeal,
  KanbanDealStage,
} from "./dealKanban.service";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import { getAuthHeaders } from "../../../utils/authUtils";
import {
  getStageDotClass,
  getStageColorValue,
  getStageColorValueByOrder,
  getStageDotClassByOrder,
} from "../../../utils/stageColors";
import ExportComponent from "../../../components/ExportComponent";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import ListFilterChips, { getClearedListFilters } from "../components/ListFilterChips";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

const MySwal = withReactContent(Swal);



interface KanbanColumn extends KanbanDealStage {
  dealCount: number;
  totalAmount: number;
}

interface DealsKanbanProps {
  onViewSwitch?: (view: 'list' | 'kanban') => void;
}

const DealsKanban = ({ onViewSwitch }: DealsKanbanProps) => {
  const { selectedTenantId } = useContext(AuthContext);
  const route = all_routes;
  const navigate = useNavigate();

  const [owner, setOwner] = useState(["Collab"]);
  const [openModal, setOpenModal] = useState(false);
  const [addDealLoading, setAddDealLoading] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsCount, setDealsCount] = useState<number | null>(null);

  const clearDealsData = () => {
    setDeals([]);
    setDealsCount(null);
    setTotalCount(0);
    setAllStages([]);
  };

  useTenantChange({
    onTenantChange: () => {
      clearDealsData();
      setCurrentPage(1);
    },
    refreshData: () => fetchKanbanData(1, true),
    clearData: clearDealsData,
  });
  const [stages, setStages] = useState<any[]>([]);

  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isAddDealOpen, setIsAddDealOpen] = useState(false);
  const [isCreatingDeal, setIsCreatingDeal] = useState(false);
  const [isUpdatingDeal, setIsUpdatingDeal] = useState(false);

  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [kanbanLoading, setKanbanLoading] = useState(true);
  const [kanbanError, setKanbanError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [allStages, setAllStages] = useState<KanbanDealStage[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [filteredDealsData, setFilteredDealsData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [kanbanScrollLeft, setKanbanScrollLeft] = useState(0);
  const [showFloatingScrollbar, setShowFloatingScrollbar] = useState(false);
  const [kanbanContainerWidth, setKanbanContainerWidth] = useState(0);
  const [kanbanContainerLeft, setKanbanContainerLeft] = useState(0);

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "title",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      pageName: "deal" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const fetchKanbanData = async (page: number = 1, reset: boolean = true) => {
    if (!selectedTenantId) return;

    try {
      if (reset) {
        setKanbanLoading(true);
        setCurrentPage(1);
      } else {
        setLoadingMore(true);
      }

      const data = await DealKanbanService.fetchKanbanData(
        parseInt(selectedTenantId),
        20,
        page,
        filters
      );

      const sortedStages = [...data.results.stages].sort(
        (a, b) => a.stage_order - b.stage_order
      );

      if (reset) {
        setAllStages(sortedStages);
        setTotalCount(data.count || 0);
        setCurrentPage(1);
      } else {
        setAllStages((prev) =>
          sortedStages.map((stage) => {
            const existing = prev.find(
              (p) =>
                p.lifecycle_type_stage_id === stage.lifecycle_type_stage_id
            );
            return {
              ...stage,
              deals: existing
                ? [...existing.deals, ...stage.deals]
                : stage.deals,
            };
          })
        );
        setCurrentPage(page);
      }

      setHasMore(!!data.next);
      setKanbanError(null);
    } catch (err) {
      console.error("Error fetching kanban data:", err);
      setKanbanError("Failed to load kanban data");
    } finally {
      setKanbanLoading(false);
      setLoadingMore(false);
    }
  };

  const flatAllDeals = useMemo(
    () => allStages.flatMap((stage) => stage.deals),
    [allStages]
  );

  const { filteredKanbanDeals, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredKanbanDeals: filteredDealsData,
        totalFilteredCount: filteredDealsData.length,
      };
    }

    return {
      filteredKanbanDeals: flatAllDeals,
      totalFilteredCount: flatAllDeals.length,
    };
  }, [flatAllDeals, filteredDealsData, isFilterActive]);

  const updateColumns = useCallback(() => {
    const kanbanColumns: KanbanColumn[] = allStages.map((stage) => {
      if (isFilterActive) {
        const stageDeals = filteredDealsData.filter(
          (deal) =>
            deal.lifecycle_type_stage?.lifecycle_type_stage_id ===
            stage.lifecycle_type_stage_id
        );
        const totalAmount = stageDeals.reduce(
          (sum, deal) => sum + (parseFloat(deal.exepected_amount || "0") || 0),
          0
        );

        return {
          ...stage,
          deals: stageDeals,
          dealCount: stageDeals.length,
          totalAmount,
        };
      }

      const totalAmount = stage.deals.reduce(
        (sum, deal) => sum + (parseFloat(deal.exepected_amount || "0") || 0),
        0
      );

      return {
        ...stage,
        dealCount: stage.count,
        totalAmount,
      };
    });

    setColumns(kanbanColumns);
  }, [allStages, filteredDealsData, isFilterActive]);

  const loadMore = async () => {
    if (!hasMore || loadingMore || isFilterActive) return;
    await fetchKanbanData(currentPage + 1, false);
  };

  useEffect(() => {
    fetchKanbanData();
  }, [selectedTenantId, filters]);

  useEffect(() => {
    updateColumns();
  }, [updateColumns]);

  useEffect(() => {
    const handleWindowScroll = () => {
      if (!hasMore || loadingMore || kanbanLoading || isFilterActive) return;
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollPosition >= documentHeight - 300) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleWindowScroll);
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [hasMore, loadingMore, kanbanLoading, isFilterActive, currentPage]);

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add");

    const handleHidden = () => {
      setIsAddDealOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const kanbanContainer = document.getElementById('kanban-container-deals');
      if (kanbanContainer) {
        const { scrollLeft, scrollWidth, clientWidth } = kanbanContainer;
        const rect = kanbanContainer.getBoundingClientRect();
        setKanbanScrollLeft(scrollLeft);
        setShowFloatingScrollbar(scrollWidth > clientWidth);
        setKanbanContainerWidth(clientWidth);
        setKanbanContainerLeft(rect.left);
      }
    };

    const setupScrollHandlers = () => {
      const kanbanContainer = document.getElementById('kanban-container-deals');
      if (kanbanContainer) {
        kanbanContainer.addEventListener('scroll', handleScroll);
        handleScroll();

        const resizeObserver = new ResizeObserver(handleScroll);
        resizeObserver.observe(kanbanContainer);

        window.addEventListener('resize', handleScroll);

        return () => {
          kanbanContainer.removeEventListener('scroll', handleScroll);
          window.removeEventListener('resize', handleScroll);
          resizeObserver.disconnect();
        };
      }
      return null;
    };

    if (columns.length > 0 && filteredKanbanDeals.length > 0) {
      const timeoutId = setTimeout(() => {
        const cleanup = setupScrollHandlers();
        if (cleanup) {
          return cleanup;
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [columns, filteredKanbanDeals]);

  const handleFloatingScrollbarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const kanbanContainer = document.getElementById('kanban-container-deals');
    if (kanbanContainer) {
      kanbanContainer.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleCreate = async (deal: Partial<DealRequest>) => {
    setIsCreatingDeal(true);
    try {
      const tenantId = parseInt(selectedTenantId || "0");
      const dealWithTenant = { ...deal, tenant_id: tenantId };
      const newDeal = await DealService.createDeal(dealWithTenant);


      fetchKanbanData(1, true);
      closeOffcanvas("offcanvas_add");

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("DEAL")} "${newDeal.title}" created successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to create deal:", err);
      showErrorAlert(getApiErrorMessage(err, "Failed to create deal. Please try again."));
      throw err;
    } finally {
      setIsCreatingDeal(false);
    }
  };

  const handleUpdate = async (id: number, deal: Partial<DealRequest>) => {
    setIsUpdatingDeal(true);
    try {
      const tenantId = parseInt(selectedTenantId || "0");
      const dealWithTenant = { ...deal, tenant_id: tenantId };
      const updatedDeal = await DealService.updateDeal(id, dealWithTenant);

      fetchKanbanData(1, true);
      closeOffcanvas("offcanvas_edit");

      setTimeout(() => {
        showSuccessAlert(`Deal "${updatedDeal.title}" updated successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to update deal:", err);
      showErrorAlert(getApiErrorMessage(err, "Failed to update deal. Please try again."));
      throw err;
    } finally {
      setIsUpdatingDeal(false);
    }
  };

  const handleDelete = async (deal: Deal) => {
    const result = await showConfirmationAlert(
      "Delete Deal",
      `Are you sure you want to delete "${deal.title}"? This action cannot be undone.`,
      async () => {
        if (deal.deal_id) {
          const tenantId = parseInt(selectedTenantId || "0");
          await DealService.deleteDeal(deal.deal_id, tenantId);
        }
      }
    );

    if (result.isConfirmed) {
      await showSuccessAlert(`Deal "${deal.title}" deleted successfully!`);
      fetchKanbanData(1, true);
    }
  };



  const handleKanbanDealClick = (kanbanDeal: KanbanDeal) => {
    navigate(`/deals/${kanbanDeal.deal_id}`);
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (!modal) return;
    const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(modal);
    if (bsOffcanvas) {
      bsOffcanvas.hide();
    } else {
      const closeButton = modal.querySelector('[data-bs-dismiss="offcanvas"]') as HTMLElement;
      if (closeButton) closeButton.click();
    }
  };

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters((prev: any) => ({
      ...prev,
      ...newFilters,
      archivedFilter: prev.archivedFilter,
    }));

    if (
      !newFilters.searchTerm &&
      !newFilters.dateRange[0] &&
      !newFilters.dateRange[1] &&
      newFilters.countryFilter.length === 0
    ) {
      setIsFilterActive(false);
      setFilteredDealsData([]);
    }
  }, []);

  const handleDropdownFilterChange = useCallback((dropdownFilters: Record<string, any>) => {
    setFilters((prev: any) => ({
      ...prev,
      ...dropdownFilters,
    }));
    setIsFilterActive(false);
    setFilteredDealsData([]);
  }, []);

  const applyListFilters = useCallback((nextFilters: Record<string, any>) => {
    setFilters(nextFilters as any);
    if (!nextFilters.searchTerm && !nextFilters.dateRange?.[0] && !nextFilters.dateRange?.[1]) {
      setIsFilterActive(false);
      setFilteredDealsData([]);
    }
  }, []);

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => {
      const newFilters: FilterState = {
        searchTerm: apiFilters.searchTerm || "",
        dateRange: apiFilters.dateRange || [null, null],
        industryFilter: apiFilters.industryFilter || [],
        countryFilter: apiFilters.countryFilter || [],
        statusFilter: apiFilters.statusFilter || null,
        sortBy: apiFilters.sortBy || "created_datetime",
        sortOrder: apiFilters.sortOrder || "desc",
      };
      setFilters((prev: any) => ({ ...prev, ...newFilters }));
    },
    []
  );

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);

        const headers = getAuthHeaders();

        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.results && Array.isArray(result.results)) {
          setFilteredDealsData(result.results);
          setIsFilterActive(true);
        } else if (result.data && Array.isArray(result.data)) {
          setFilteredDealsData(result.data);
          setIsFilterActive(true);
        } else if (Array.isArray(result)) {
          setFilteredDealsData(result);
          setIsFilterActive(true);
        } else {
          setFilteredDealsData([]);
          setIsFilterActive(true);
        }
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");
          localStorage.removeItem("tenant_id");

          navigate("/login");
          return;
        }

        setFilteredDealsData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId, navigate]
  );

  const handleFiltersReset = useCallback(() => {
    setFilters({
      searchTerm: "",
      dateRange: [null, null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
      archivedFilter: "no",
    });
    setIsFilterActive(false);
    setFilteredDealsData([]);
  }, []);

  const onDragEnd = async (result: any) => {
    if (!result.destination || !selectedTenantId) return;

    const { source, destination } = result;

    if (source.droppableId === destination.droppableId) {
      const colIdx = columns.findIndex(
        (col) => col.lifecycle_type_stage_id.toString() === source.droppableId
      );
      const newDeals = Array.from(columns[colIdx].deals);
      const [removed] = newDeals.splice(source.index, 1);
      newDeals.splice(destination.index, 0, removed);

      const newColumns = [...columns];
      newColumns[colIdx] = { ...columns[colIdx], deals: newDeals };
      setColumns(newColumns);
    } else {
      const sourceColIdx = columns.findIndex(
        (col) => col.lifecycle_type_stage_id.toString() === source.droppableId
      );
      const destColIdx = columns.findIndex(
        (col) =>
          col.lifecycle_type_stage_id.toString() === destination.droppableId
      );

      const sourceDeals = Array.from(columns[sourceColIdx].deals);
      const destDeals = Array.from(columns[destColIdx].deals);

      const [removed] = sourceDeals.splice(source.index, 1);
      destDeals.splice(destination.index, 0, removed);

      const newColumns = [...columns];
      newColumns[sourceColIdx] = {
        ...columns[sourceColIdx],
        deals: sourceDeals,
        dealCount: sourceDeals.length,
        totalAmount: sourceDeals.reduce(
          (sum, deal) => sum + (parseFloat(deal.exepected_amount || "0") || 0),
          0
        ),
      };
      newColumns[destColIdx] = {
        ...columns[destColIdx],
        deals: destDeals,
        dealCount: destDeals.length,
        totalAmount: destDeals.reduce(
          (sum, deal) => sum + (parseFloat(deal.exepected_amount || "0") || 0),
          0
        ),
      };
      setColumns(newColumns);

      try {
        await DealKanbanService.updateDealStage(
          removed.deal_id,
          columns[destColIdx].lifecycle_type_stage_id,
          parseInt(selectedTenantId)
        );

        setAllStages((prev) =>
          prev.map((stage) => {
            if (
              stage.lifecycle_type_stage_id ===
              columns[sourceColIdx].lifecycle_type_stage_id
            ) {
              return {
                ...stage,
                deals: stage.deals.filter(
                  (deal) => deal.deal_id !== removed.deal_id
                ),
                count: Math.max(0, stage.count - 1),
              };
            }
            if (
              stage.lifecycle_type_stage_id ===
              columns[destColIdx].lifecycle_type_stage_id
            ) {
              return {
                ...stage,
                deals: [
                  ...stage.deals,
                  {
                    ...removed,
                    lifecycle_type_stage: {
                      ...(removed.lifecycle_type_stage || {}),
                      lifecycle_type_stage_id:
                        columns[destColIdx].lifecycle_type_stage_id,
                      stage_name: columns[destColIdx].stage_name,
                    },
                  },
                ],
                count: stage.count + 1,
              };
            }
            return stage;
          })
        );

        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: `Deal "${removed.title}" moved to ${columns[destColIdx].stage_name} successfully!`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } catch (error) {
        console.error("Error updating deal stage:", error);
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "error",
          title: "Failed to update deal stage. Please try again.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        fetchKanbanData(1, true);
      }
    }
  };

  return (
    <>
      <style>{`
        #kanban-container-deals::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <DealFormDataProvider isMainApiCompleted={!kanbanLoading} shouldLoad={isAddDealOpen || !!selectedDeal}>
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        {getLifecycleTypeName("DEAL")}
                        <span className="count-title">{totalCount}</span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col">
                        <ListFilterChips
                          entityType="deal"
                          filters={filters}
                          onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                          onClearAll={() => applyListFilters(getClearedListFilters())}
                          disabled={kanbanLoading}
                          className="mb-0"
                        />
                      </div>
                      <div className="col-sm-auto">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          <ExportComponent entityType="deal" filters={filters} />
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add"
                            onClick={() => setIsAddDealOpen(true)}
                          >
                            <i className="ti ti-square-rounded-plus me-2" />
                            Add {getLifecycleTypeName("DEAL")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={flatAllDeals}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      onApiCall={handleApiCall}
                      onFiltersReset={handleFiltersReset}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <LifecycleFilterDropdown
                        entityType="deal"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={{ ...filters }}
                        loading={kanbanLoading}
                      />
                      <div className="view-icons ms-3">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch?.('list');
                          }}
                        >
                          <i className="ti ti-list-tree" />
                        </Link>
                        <Link to="#" className="active">
                          <i className="ti ti-layout-kanban" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    {kanbanLoading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading {getLifecycleTypeName("DEAL")} Kanban...
                          </small>
                        </div>
                      </div>
                    ) : kanbanError ? (
                      <div className="alert alert-danger text-center">
                        <i className="ti ti-alert-circle me-2"></i>
                        {kanbanError}
                        <button
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => fetchKanbanData(1, true)}
                        >
                          Retry
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ position: "relative" }}>
                          {isFilterLoading && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 10,
                                minHeight: "200px",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <span>Filtering results...</span>
                              </div>
                            </div>
                          )}
                          {filteredKanbanDeals.length === 0 ? (
                            <div className="text-center py-5">
                              <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                                <i className="ti ti-medal fs-36 text-gray"></i>
                              </div>
                              <h4 className="text-gray mb-2">No {getLifecycleTypeName("DEAL")} Found!</h4>
                              <p className="text-muted mb-3">
                                There are no {getLifecycleTypePluralName("DEAL").toLowerCase()} to display at the moment.
                              </p>
                              <button
                                className="btn btn-primary"
                                data-bs-toggle="offcanvas"
                                data-bs-target="#offcanvas_add"
                                onClick={() => setIsAddDealOpen(true)}
                              >
                                <i className="fa fa-plus me-2" />
                                Add Your First {getLifecycleTypeName("DEAL")}
                              </button>
                            </div>
                          ) : (
                            <DragDropContext onDragEnd={onDragEnd}>
                              <div
                                id="kanban-container-deals"
                                className="d-flex overflow-x-auto align-items-start mb-0 gap-3"
                                style={{
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none'
                                }}
                              >
                                {columns.map((col, colIdx) => (
                                  <div
                                    className="kanban-list-items p-2 rounded border"
                                    key={col.lifecycle_type_stage_id}
                                    style={{
                                      minWidth: 300,
                                      borderTop: `4px solid ${getStageColorValueByOrder(
                                        col.stage_order
                                      )}`,
                                    }}
                                  >
                                    <div className="card mb-0 border-0 shadow">
                                      <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            <h6 className="d-flex align-items-center mb-1">
                                              <i
                                                className={`ti ti-circle-filled fs-10 ${getStageDotClassByOrder(
                                                  col.stage_order
                                                )} me-1`}
                                              />
                                              {col.stage_name}
                                            </h6>
                                            <span>
                                              {col.dealCount}{" "}
                                              {getLifecycleTypePluralName("DEAL")}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <Droppable
                                      droppableId={col.lifecycle_type_stage_id.toString()}
                                    >
                                      {(provided: any) => (
                                        <div
                                          className="kanban-drag-wrap"
                                          ref={provided.innerRef}
                                          {...provided.droppableProps}
                                          style={{
                                            minHeight: 100,
                                            borderTop: `4px solid ${getStageColorValueByOrder(
                                              col.stage_order
                                            )}`,
                                          }}
                                        >
                                          {col.deals.map((deal, idx) => (
                                            <Draggable
                                              draggableId={deal.deal_id.toString()}
                                              index={idx}
                                              key={deal.deal_id}
                                            >
                                              {(provided: any, snapshot: any) => (
                                                <div
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  {...provided.dragHandleProps}
                                                  style={{
                                                    ...provided.draggableProps
                                                      .style,
                                                    marginBottom: 16,
                                                    opacity: snapshot.isDragging
                                                      ? 0.8
                                                      : 1,
                                                  }}
                                                >
                                                  <div className="card kanban-card border mb-0 mt-3 shadow">
                                                    <div className="card-body">
                                                      <div className="d-block">
                                                        <div className="d-flex align-items-center mb-3">
                                                          <Link
                                                            to={`/deals/${deal.deal_id}`}
                                                            className={`avatar avatar-sm bg-primary rounded-circle flex-shrink-0 me-2`}
                                                            onClick={() =>
                                                              handleKanbanDealClick(
                                                                deal
                                                              )
                                                            }
                                                          >
                                                            <i
                                                              className="ti ti-medal"
                                                              style={{
                                                                color: "#ffffff",
                                                                fontSize: "1rem",
                                                              }}
                                                            />
                                                          </Link>
                                                          <h6 className="fw-medium fs-14 mb-0">
                                                            <Link
                                                              to={`/deals/${deal.deal_id}`}
                                                              onClick={() =>
                                                                handleKanbanDealClick(
                                                                  deal
                                                                )
                                                              }
                                                            >
                                                                {deal.title?.length > 30 ? (
                                                                  <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip id={`tooltip-${deal.deal_id}`}>{deal.title}</Tooltip>}
                                                                  >
                                                                    <span>{`${deal.title.substring(0, 30)}...`}</span>
                                                                  </OverlayTrigger>
                                                                ) : (
                                                                deal.title
                                                              )}
                                                            </Link>
                                                          </h6>
                                                        </div>
                                                      </div>
                                                      <div className="d-flex flex-column">
                                                        <p className="text-default d-inline-flex align-items-center mb-2">
                                                          <i className="ti ti-report-money text-dark me-1" />
                                                          {deal.estimated_value ||
                                                            "No Estimated Value"}{" "}
                                                          {deal.estimated_value
                                                            ? deal.currency_code
                                                            : " "}
                                                        </p>
                                                        <p className="text-default d-inline-flex align-items-center mb-2">
                                                          <i className="ti ti-building text-dark me-1" />
                                                          {deal.company?.company_name ? (
                                                              deal.company.company_name.length > 30 ? (
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={<Tooltip id={`tooltip-company-${deal.deal_id}`}>{deal.company.company_name}</Tooltip>}
                                                                >
                                                                  <span>{`${deal.company.company_name.substring(0, 30)}...`}</span>
                                                                </OverlayTrigger>
                                                              ) : (
                                                                deal.company.company_name
                                                              )
                                                            ) : (
                                                              "No Company"
                                                            )}
                                                        </p>
                                                        <p className="text-default d-inline-flex align-items-center mb-2">
                                                          <i className="ti ti-user text-dark me-1" />
                                                          {deal.primary_contact?.full_name ? (
                                                              deal.primary_contact.full_name.length > 30 ? (
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={<Tooltip id={`tooltip-contact-${deal.deal_id}`}>{deal.primary_contact.full_name}</Tooltip>}
                                                                >
                                                                  <span>{`${deal.primary_contact.full_name.substring(0, 30)}...`}</span>
                                                                </OverlayTrigger>
                                                              ) : (
                                                                deal.primary_contact.full_name
                                                              )
                                                            ) : (
                                                              "No Contact"
                                                            )}
                                                        </p>
                                                        <p className="text-default d-inline-flex align-items-center">
                                                          <i className="fa-solid fa-code-branch me-1" style={{ color: "rgba(71, 71, 88, 1)", fontSize: "12px", marginLeft: "3px", }}></i>
                                                          {deal.source ||
                                                            "No Source"}
                                                        </p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </Draggable>
                                          ))}
                                          {provided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                  </div>
                                ))}
                              </div>

                              {loadingMore && (
                                <div className="text-center mt-4">
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Loading more {getLifecycleTypePluralName("DEAL")}...
                                </div>
                              )}

                              {showFloatingScrollbar && (
                                <div
                                  style={{
                                    position: 'fixed',
                                    bottom: '5px',
                                    left: `${kanbanContainerLeft}px`,
                                    width: `${kanbanContainerWidth}px`,
                                    height: '12px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    borderRadius: '6px',
                                    zIndex: 1000,
                                    padding: '2px',
                                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '100%',
                                      height: '8px',
                                      overflowX: 'auto',
                                      overflowY: 'hidden'
                                    }}
                                    onScroll={handleFloatingScrollbarScroll}
                                  >
                                    <div
                                      style={{
                                        width: `${(document.getElementById('kanban-container-deals')?.scrollWidth || 0)}px`,
                                        height: '1px'
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </DragDropContext>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="offcanvas_add"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              Add New {getLifecycleTypeName("DEAL")}
            </h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => setSelectedDeal(null)}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {isAddDealOpen && (
              <DealForm
                onSubmit={handleCreate}
                onCancel={() => {
                  closeOffcanvas("offcanvas_add");
                }}
                isSubmitting={isCreatingDeal}
              />
            )}
          </div>
        </div>

        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="offcanvas_edit"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Edit {getLifecycleTypeName("DEAL")}</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {selectedDeal && (
              <DealForm
                deal={selectedDeal ?? undefined}
                onSubmit={(formData) => {
                  if (selectedDeal?.deal_id) {
                    return handleUpdate(selectedDeal.deal_id, formData);
                  }
                  return Promise.resolve();
                }}
                onCancel={() => {
                  closeOffcanvas("offcanvas_edit");
                }}
                isSubmitting={isUpdatingDeal}
              />
            )}
          </div>
        </div>
      </DealFormDataProvider>
    </>
  );
};

export default DealsKanban;
